/**
 * Bootstrap timing utility for diagnosing slow builder loads.
 *
 * Silent unless explicitly enabled:
 *   localStorage.setItem('blazorly:debug-timings', '1')
 * then reload and watch the console for `[Bootstrap ...]` lines.
 *
 * Uses console.info so it never trips the console.log/debug budget, and every
 * call site is a no-op (one cached flag check) when disabled.
 */

const TIMINGS_STORAGE_KEY = 'blazorly:debug-timings';

let enabledCache: boolean | null = null;
let t0 = 0;
const marks = new Map<string, number>();

export function timingsEnabled(): boolean {
  if (enabledCache === null) {
    try {
      enabledCache = localStorage.getItem(TIMINGS_STORAGE_KEY) === '1';
    } catch {
      enabledCache = false;
    }
  }
  return enabledCache;
}

/** Reset cached state (tests only). */
export function resetTimingsForTests(): void {
  enabledCache = null;
  t0 = 0;
  marks.clear();
}

function elapsedMs(): number {
  if (t0 === 0) t0 = performance.now();
  return performance.now() - t0;
}

/** Log a completed span with total elapsed + the span's own duration. */
export function reportTiming(label: string, durationMs: number): void {
  if (!timingsEnabled()) return;
  console.info(
    `[Bootstrap +${(elapsedMs() / 1000).toFixed(1)}s] ${label}: ${Math.round(durationMs)}ms`
  );
}

/** Record a named timestamp for later measureTiming(). */
export function markTiming(name: string): void {
  if (!timingsEnabled()) return;
  marks.set(name, performance.now());
}

/** Report the duration since markTiming(startMark). No-op if unmarked. */
export function measureTiming(label: string, startMark: string): void {
  if (!timingsEnabled()) return;
  const start = marks.get(startMark);
  if (start === undefined) return;
  reportTiming(label, performance.now() - start);
}

/** Run fn, then report how long it took. */
export async function timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
  if (!timingsEnabled()) return fn();
  const start = performance.now();
  try {
    return await fn();
  } finally {
    reportTiming(label, performance.now() - start);
  }
}
